import React, { useRef, useState } from 'react';
import { Upload, Loader2, Sparkles, ShieldAlert, CheckCircle2, X, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { api } from '../services/api';

const CONFIDENCE_THRESHOLD = 95;
const HIGH_VALUE_THRESHOLD = 1_000_000;

/**
 * AI Document Scanner for insurance schedules.
 * 6 safety patterns:
 *  1. Review-Before-Save — onApply only runs after user clicks "Apply to form".
 *  2. Source pinning — page + verbatim snippet rendered under each field.
 *  3. Confidence-based amber flagging — fields <95% require an explicit confirm tick.
 *  4. £1M+ double-confirm via a typed match; UK date strictly DD/MM/YYYY.
 *  5. AI-suggested badge — applied fields are tagged so the form can show the chip.
 *  6. Audit log — written server-side on extraction.
 */
export function InsuranceOCRReview({ onApply, currentInsuredValue }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // editable working copy of extracted values
  const [edits, setEdits] = useState({});
  // explicit "I have checked this" ticks for low-confidence fields
  const [confirms, setConfirms] = useState({});
  // typed double-confirm for high-value sum_insured
  const [bigValueConfirmInput, setBigValueConfirmInput] = useState('');

  const reset = () => {
    setResult(null);
    setEdits({});
    setConfirms({});
    setBigValueConfirmInput('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PDF, JPEG, PNG, or WebP file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large. Max 8 MB.');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);

    setUploading(true);
    try {
      const res = await api.extractInsurance(fd);
      const data = res.data;
      setResult(data);

      // initialise editable values from extraction
      setEdits({
        insurance_provider: data.insurance_provider.value || '',
        policy_number: data.policy_number.value || '',
        sum_insured: data.sum_insured.value != null ? String(data.sum_insured.value) : '',
        renewal_date: data.renewal_date.value || '', // DD/MM/YYYY
      });
      setConfirms({});
      setBigValueConfirmInput('');

      const foundCount = [
        data.insurance_provider.found,
        data.policy_number.found,
        data.sum_insured.found,
        data.renewal_date.found,
      ].filter(Boolean).length;

      if (foundCount === 0) {
        toast.error("Couldn't find any insurance details. Try a clearer scan.");
      } else {
        toast.success(`Found ${foundCount} of 4 fields. Please review before applying.`);
      }
    } catch (err) {
      console.error('Insurance OCR failed', err);
      toast.error(err.response?.data?.detail || 'Document scan failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const fieldNeedsConfirm = (key) => {
    if (!result) return false;
    const f = result[key];
    if (!f || !f.found) return false;
    const userValue = edits[key];
    // If the user has edited the value, the AI suggestion no longer needs amber confirm
    const original = key === 'sum_insured' ? String(f.value) : f.value;
    if (String(userValue ?? '') !== String(original ?? '')) return false;
    return (f.confidence || 0) < CONFIDENCE_THRESHOLD;
  };

  const sumInsuredNumber = () => {
    const n = parseFloat(edits.sum_insured);
    return Number.isFinite(n) ? n : null;
  };

  const ukDateValid = (s) => /^\d{2}\/\d{2}\/\d{4}$/.test(s || '');

  const ukToISO = (s) => {
    const m = (s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return '';
    return `${m[3]}-${m[2]}-${m[1]}`;
  };

  const handleApply = () => {
    if (!result) return;

    // Validate UK date if present
    if (edits.renewal_date && !ukDateValid(edits.renewal_date)) {
      toast.error('Renewal date must be DD/MM/YYYY.');
      return;
    }

    // Validate sum_insured is numeric if present
    const sum = sumInsuredNumber();
    if (edits.sum_insured && sum === null) {
      toast.error('Sum insured must be a number.');
      return;
    }

    // Amber confirms — require tick for any AI-suggested field below threshold
    for (const key of ['insurance_provider', 'policy_number', 'sum_insured', 'renewal_date']) {
      if (fieldNeedsConfirm(key) && !confirms[key]) {
        toast.error('Please tick the amber confirmation boxes before applying.');
        return;
      }
    }

    // High-value double-confirm
    if (sum !== null && sum >= HIGH_VALUE_THRESHOLD) {
      const expected = String(Math.round(sum));
      if (bigValueConfirmInput.replace(/[^\d]/g, '') !== expected) {
        toast.error(`This is a high-value figure. Type ${expected} to confirm.`);
        return;
      }
    }

    // Build the patch + ai_suggested flag set
    const patch = {};
    const aiSuggested = [];

    if (edits.insurance_provider) {
      patch.insurance_provider = edits.insurance_provider.trim();
      if (patch.insurance_provider === (result.insurance_provider.value || '')) {
        aiSuggested.push('insurance_provider');
      }
    }
    if (edits.policy_number) {
      patch.insurance_policy = edits.policy_number.trim();
      if (patch.insurance_policy === (result.policy_number.value || '')) {
        aiSuggested.push('insurance_policy');
      }
    }
    if (sum !== null) {
      patch.insured_value = String(sum);
      if (sum === result.sum_insured.value) {
        aiSuggested.push('insured_value');
      }
    }
    if (edits.renewal_date) {
      patch.insurance_expiry = ukToISO(edits.renewal_date);
      if (edits.renewal_date === (result.renewal_date.value || '')) {
        aiSuggested.push('insurance_expiry');
      }
    }

    onApply(patch, aiSuggested);
    toast.success('Applied to form. Review the gold "AI-suggested" chips and save.');
    reset();
  };

  // ---- Render ----
  if (!result) {
    return (
      <div className="rounded-lg border border-dashed border-purple-500/30 bg-purple-900/5 p-4" data-testid="insurance-ocr-empty">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-purple-500/10 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium">Scan an insurance schedule</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              AI will auto-fill the fields below from your document. Take a moment to review each value before saving.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-xs px-3 py-2 h-auto shrink-0"
            data-testid="insurance-ocr-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <Upload size={14} className="mr-1.5" />
                Upload document
              </>
            )}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
          data-testid="insurance-ocr-file-input"
        />
      </div>
    );
  }

  const sum = sumInsuredNumber();
  const requiresBigConfirm = sum !== null && sum >= HIGH_VALUE_THRESHOLD;

  return (
    <div className="rounded-lg border border-purple-500/30 bg-gradient-to-br from-purple-900/15 to-blue-900/10 p-4 space-y-4" data-testid="insurance-ocr-review">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <FileText size={18} className="text-purple-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-white text-sm font-medium">Review AI suggestions</p>
            <p className="text-xs text-muted-foreground truncate">
              {result.filename} · {result.document_summary || 'Insurance document'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-muted-foreground hover:text-white shrink-0"
          data-testid="insurance-ocr-cancel"
          aria-label="Cancel review"
        >
          <X size={16} />
        </button>
      </div>

      <FieldRow
        testId="ocr-field-provider"
        label="Provider"
        field={result.insurance_provider}
        value={edits.insurance_provider}
        onChange={(v) => setEdits((p) => ({ ...p, insurance_provider: v }))}
        needsConfirm={fieldNeedsConfirm('insurance_provider')}
        confirmed={!!confirms.insurance_provider}
        onConfirm={(v) => setConfirms((p) => ({ ...p, insurance_provider: v }))}
      />

      <FieldRow
        testId="ocr-field-policy"
        label="Policy number"
        field={result.policy_number}
        value={edits.policy_number}
        onChange={(v) => setEdits((p) => ({ ...p, policy_number: v }))}
        needsConfirm={fieldNeedsConfirm('policy_number')}
        confirmed={!!confirms.policy_number}
        onConfirm={(v) => setConfirms((p) => ({ ...p, policy_number: v }))}
      />

      <FieldRow
        testId="ocr-field-sum-insured"
        label="Sum insured (£)"
        field={result.sum_insured}
        value={edits.sum_insured}
        onChange={(v) => setEdits((p) => ({ ...p, sum_insured: v.replace(/[^\d.]/g, '') }))}
        inputMode="decimal"
        placeholder="e.g. 2500000"
        needsConfirm={fieldNeedsConfirm('sum_insured')}
        confirmed={!!confirms.sum_insured}
        onConfirm={(v) => setConfirms((p) => ({ ...p, sum_insured: v }))}
        displayValueFn={(raw) => {
          const n = parseFloat(raw);
          return Number.isFinite(n) ? `£${n.toLocaleString('en-GB')}` : null;
        }}
      />

      {requiresBigConfirm && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="ocr-big-value-block">
          <div className="flex items-start gap-2">
            <ShieldAlert size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-amber-300 text-xs font-medium">High-value sum insured</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                You're about to apply <span className="text-white font-mono">£{Math.round(sum).toLocaleString('en-GB')}</span>. To confirm this is correct, please re-type the number (digits only).
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={bigValueConfirmInput}
                onChange={(e) => setBigValueConfirmInput(e.target.value)}
                placeholder={String(Math.round(sum))}
                className="mt-2 w-full bg-black/30 border border-amber-500/30 rounded px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-amber-400"
                data-testid="ocr-big-value-confirm-input"
              />
            </div>
          </div>
        </div>
      )}

      <FieldRow
        testId="ocr-field-renewal"
        label="Renewal date (DD/MM/YYYY)"
        field={result.renewal_date}
        value={edits.renewal_date}
        onChange={(v) => setEdits((p) => ({ ...p, renewal_date: v }))}
        placeholder="DD/MM/YYYY"
        needsConfirm={fieldNeedsConfirm('renewal_date')}
        confirmed={!!confirms.renewal_date}
        onConfirm={(v) => setConfirms((p) => ({ ...p, renewal_date: v }))}
      />

      <div className="flex gap-2 pt-2 border-t border-purple-500/10">
        <Button
          type="button"
          onClick={handleApply}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm flex-1"
          data-testid="insurance-ocr-apply-btn"
        >
          <CheckCircle2 size={14} className="mr-1.5" />
          Apply to form
        </Button>
        <Button
          type="button"
          onClick={reset}
          className="btn-secondary text-sm"
          data-testid="insurance-ocr-discard-btn"
        >
          Discard
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        AI extraction is a suggestion — nothing is saved until you press <span className="text-white">Add Asset</span>. We log every scan to your activity log.
      </p>
    </div>
  );
}

// ---- Field sub-component ----

function FieldRow({
  testId,
  label,
  field,
  value,
  onChange,
  needsConfirm,
  confirmed,
  onConfirm,
  placeholder = '',
  inputMode,
  displayValueFn,
}) {
  const found = field?.found;
  const confidence = field?.confidence ?? 0;

  const confidenceColor =
    confidence >= CONFIDENCE_THRESHOLD ? 'text-emerald-400' :
    confidence >= 70 ? 'text-amber-300' :
    confidence > 0 ? 'text-rose-300' :
    'text-muted-foreground';

  const display = displayValueFn ? displayValueFn(value) : null;

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
        {found ? (
          <span className={`text-[10px] uppercase tracking-wider ${confidenceColor}`}>
            {confidence}% confidence
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Not found — please type manually</span>
        )}
      </div>

      <input
        type="text"
        inputMode={inputMode}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || (found ? '' : 'Type here')}
        className={`luxury-input w-full text-sm ${needsConfirm ? 'border-amber-500/50' : ''}`}
        data-testid={`${testId}-input`}
      />

      {display && (
        <p className="text-[11px] text-muted-foreground">{display}</p>
      )}

      {found && field.source_snippet && (
        <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`${testId}-source`}>
          <span className="text-purple-300/80">Page {field.source_page}:</span>{' '}
          <span className="italic">“{field.source_snippet}”</span>
        </p>
      )}

      {needsConfirm && (
        <label className="flex items-start gap-2 mt-1 cursor-pointer" data-testid={`${testId}-amber-confirm`}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
            className="mt-0.5 accent-amber-400"
            data-testid={`${testId}-amber-confirm-input`}
          />
          <span className="text-[11px] text-amber-300/90 leading-snug">
            Low confidence — I've checked the value against the document.
          </span>
        </label>
      )}
    </div>
  );
}
